// ============================================================
// GRID CODEC - a voxel grid small enough to save.
//
// Firestore stores a map field as ONE INDEXED FIELD PER KEY. A grid of
// `{"x,y,z": "stone"}` therefore costs an index entry per cube, and past
// roughly forty thousand of them the write is REJECTED - which is what
// made a forty-layer tower vanish "after some moments": the write failed,
// the failure was logged to a console nobody was watching, and the
// listener then delivered the server's older copy.
//
// Packed, the whole grid is a single string: one field, one index entry,
// and a megabyte of room. Run-length encoding suits this data unusually
// well, because THE THING THAT MAKES A STRUCTURE BIG IS THE THING THAT
// MAKES IT COMPRESSIBLE - a solid tower is four hundred thousand cells
// and a few hundred runs.
//
// The format is deliberately plain text: legible in the Firestore
// console, diffable, and impossible to get subtly wrong in a way a
// round-trip test would miss.
//
//   "1;stone,earth;0,0,0:40x1;1,0,0:40x1"
//    │  │                │     │  │
//    │  │                │     │  └── run length
//    │  │                │     └───── material index into the palette
//    │  │                └─────────── run start, "x,y,z"
//    │  └──────────────────────────── palette, in first-seen order
//    └─────────────────────────────── format version
//
// Runs advance along Y first, because a tower is a column and a column
// is what people build.
//
// Pure. Zero React dependency.
// ============================================================
import type { VoxelGrid } from './voxels';
import { cellKey, parseCell } from './voxels';

/** Bumped only if the format changes shape. Readers refuse what they do not know. */
const VERSION = '1';

export interface PackedCells {
  /** The whole grid as one field. */
  packed: string;
  /** How many cells it describes, for anything that wants to report size. */
  count: number;
}

/**
 * Cells in the order runs are built: by column, then upward.
 *
 * Sorting by (x, z, y) puts a tower's cubes next to each other, which is
 * what makes the run lengths long.
 */
function sortedCells(cells: Record<string, string>): { x: number; y: number; z: number; material: string }[] {
  return Object.entries(cells)
    .map(([key, material]) => ({ ...parseCell(key), material }))
    .sort((a, b) => a.x - b.x || a.z - b.z || a.y - b.y);
}

/** Pack a cell map into one string. */
export function packCells(cells: Record<string, string> | undefined): PackedCells {
  const all = sortedCells(cells ?? {});
  if (!all.length) return { packed: '', count: 0 };

  // The palette fills in first-seen order, which the packed string records.
  const palette: string[] = [];
  const indexOf = (material: string) => {
    const at = palette.indexOf(material);
    if (at >= 0) return at;
    palette.push(material);
    return palette.length - 1;
  };

  const runs: string[] = [];
  let run: Run = { start: all[0], material: indexOf(all[0].material), length: 1 };
  for (const cell of all.slice(1)) {
    const index = indexOf(cell.material);
    if (continuesRun(run, cell, index)) {
      run.length += 1;
      continue;
    }
    runs.push(runText(run));
    run = { start: cell, material: index, length: 1 };
  }
  runs.push(runText(run));

  return { packed: [VERSION, palette.join(','), runs.join(';')].join(';'), count: all.length };
}

/** A vertical run of one material: where it starts, which palette entry, how tall. */
interface Run {
  start: { x: number; y: number; z: number };
  material: number;
  length: number;
}

/** Is this cell the next one up the same column, in the same material? */
function continuesRun(run: Run, cell: { x: number; y: number; z: number }, material: number): boolean {
  const { start } = run;
  return cell.x === start.x && cell.z === start.z && cell.y === start.y + run.length && material === run.material;
}

function runText({ start, material, length }: Run): string {
  return `${start.x},${start.y},${start.z}:${material}x${length}`;
}

/** Unpack a string back into a cell map. Returns {} for anything unreadable. */
export function unpackCells(packed: string | undefined): Record<string, string> {
  if (!packed) return {};
  const [version, paletteText, ...runTexts] = packed.split(';');
  if (version !== VERSION) return {};
  const palette = paletteText ? paletteText.split(',') : [];

  const cells: Record<string, string> = {};
  for (const text of runTexts) {
    const run = readRun(text, palette);
    if (run) fillRun(cells, run);
  }
  return cells;
}

/** Write a run's cells, bottom up. */
function fillRun(
  cells: Record<string, string>,
  run: { x: number; y: number; z: number; material: string; length: number },
): void {
  for (let i = 0; i < run.length; i++) cells[cellKey(run.x, run.y + i, run.z)] = run.material;
}

/** One `x,y,z:paletteIndex x length` run, or null when it is malformed or names no material. */
function readRun(
  text: string,
  palette: string[],
): { x: number; y: number; z: number; material: string; length: number } | null {
  if (!text) return null;
  const [at, tail] = text.split(':');
  if (!at || !tail) return null;
  const [x, y, z] = at.split(',').map(Number);
  const [indexText, lengthText] = tail.split('x');
  const material = palette[Number(indexText)];
  const length = Number(lengthText);
  if (material === undefined || !Number.isFinite(length)) return null;
  return { x, y, z, material, length };
}

/**
 * A grid on its way to Firestore.
 *
 * `cells` is replaced by `packedCells`, so the document carries one field
 * where it used to carry one per cube.
 */
export function packGrid(grid: VoxelGrid | undefined): Record<string, unknown> | undefined {
  if (!grid) return undefined;
  const { cells, ...rest } = grid;
  return { ...rest, packedCells: packCells(cells).packed };
}

/**
 * A grid on its way back.
 *
 * Reads BOTH SHAPES: a packed grid, and the plain map every map saved
 * before v7.35.0 still uses. Nothing has to be migrated, and a map
 * written by an older client keeps working.
 */
export function unpackGrid(stored: (Record<string, unknown> & { cells?: unknown }) | undefined): VoxelGrid | undefined {
  if (!stored) return undefined;
  const { packedCells, ...rest } = stored as { packedCells?: string } & Record<string, unknown>;
  if (typeof packedCells === 'string') {
    return { ...(rest as unknown as VoxelGrid), cells: unpackCells(packedCells) };
  }
  return stored as unknown as VoxelGrid;
}
